// Calculos sobre las llamadas del CRM. Funciones puras (se prueban sin servidor).

import type { Llamada, Tarea } from '../api/types';
import { searchKey } from '../lib/phone-number';

const HORA = 3_600_000;
const DIA = 24 * HORA;

const ts = (l: Pick<Llamada, 'inicio' | 'creado'>) => new Date(l.inicio ?? l.creado).getTime();

export const esMia = (l: Llamada, extension?: string) => !extension || l.agente === extension;

export const esContestada = (l: Llamada) => l.estado === 'CONTESTADA' && l.duracion > 0;
export const esPerdida = (l: Llamada) => l.estado !== 'CONTESTADA' && l.direccion === 'ENTRANTE';

// Llamadas contestadas que aun no tienen tipificacion (ultimos N dias).
export function porTipificar(llamadas: Llamada[], { extension, dias = 7, ahora = Date.now() }: { extension?: string; dias?: number; ahora?: number } = {}) {
  return llamadas
    .filter((l) => l.gestion === 'PENDIENTE' && esContestada(l) && l.direccion !== 'INTERNA' && esMia(l, extension) && ahora - ts(l) <= dias * DIA)
    .sort((a, b) => ts(b) - ts(a));
}

// Entrantes perdidas a las que nadie ha devuelto la llamada todavia.
// Se considera devuelta si despues hubo una llamada contestada (en cualquier sentido) con el mismo numero.
export function porDevolver(llamadas: Llamada[], { horas = 72, ahora = Date.now() }: { horas?: number; ahora?: number } = {}) {
  const ultimaAtendida = new Map<string, number>();
  for (const l of llamadas) {
    if (!esContestada(l) && l.direccion !== 'SALIENTE') continue;
    const k = searchKey(l.numero);
    if (k.length < 7) continue;
    ultimaAtendida.set(k, Math.max(ultimaAtendida.get(k) ?? 0, ts(l)));
  }
  const vistas = new Set<string>();
  return llamadas
    .filter((l) => esPerdida(l) && ahora - ts(l) <= horas * HORA)
    .sort((a, b) => ts(b) - ts(a))
    .filter((l) => {
      const k = searchKey(l.numero);
      if (k.length < 7 || vistas.has(k)) return false;
      vistas.add(k);
      return (ultimaAtendida.get(k) ?? 0) < ts(l);
    });
}

export function delDia(llamadas: Llamada[], dia = new Date()) {
  const d = dia.toDateString();
  return llamadas.filter((l) => new Date(ts(l)).toDateString() === d);
}

export type KpisDia = { total: number; contestadas: number; perdidas: number; tiempoMedio: number; tipificadasPct: number; minutos: number };

export function kpis(llamadas: Llamada[]): KpisDia {
  const utiles = llamadas.filter((l) => l.direccion !== 'INTERNA');
  const contestadas = utiles.filter(esContestada);
  const hablado = contestadas.reduce((a, l) => a + l.duracion, 0);
  const tipificadas = contestadas.filter((l) => l.gestion !== 'PENDIENTE').length;
  return {
    total: utiles.length,
    contestadas: contestadas.length,
    perdidas: utiles.filter(esPerdida).length,
    tiempoMedio: contestadas.length ? Math.round(hablado / contestadas.length) : 0,
    tipificadasPct: contestadas.length ? Math.round((100 * tipificadas) / contestadas.length) : 100,
    minutos: Math.round(hablado / 60),
  };
}

// Serie diaria de los ultimos N dias (para las mini graficas de tendencia).
export function serieDiaria(llamadas: Llamada[], dias: number, valor: (ls: Llamada[]) => number, hoy = new Date()) {
  const out: number[] = [];
  for (let i = dias - 1; i >= 0; i--) {
    const d = new Date(hoy);
    d.setDate(d.getDate() - i);
    out.push(valor(delDia(llamadas, d)));
  }
  return out;
}

export function porHora(llamadas: Llamada[], desde = 7, hasta = 19) {
  const horas = Array.from({ length: hasta - desde + 1 }, (_, i) => desde + i);
  const cuenta = horas.map((h) => llamadas.filter((l) => new Date(ts(l)).getHours() === h).length);
  const etiquetas = horas.map((h) => String(h > 12 ? h - 12 : h));
  return { cuenta, etiquetas, horaActual: horas.indexOf(new Date().getHours()) };
}

/* ---------------- tarificador ---------------- */

export const salientesConCosto = (llamadas: Llamada[]) => llamadas.filter((l) => l.direccion === 'SALIENTE' && l.duracion > 0);

export function costoPorDia(llamadas: Llamada[], dias = 14, hoy = new Date()) {
  const valores: number[] = [];
  const etiquetas: string[] = [];
  for (let i = dias - 1; i >= 0; i--) {
    const d = new Date(hoy);
    d.setDate(d.getDate() - i);
    etiquetas.push(String(d.getDate()));
    valores.push(delDia(llamadas, d).reduce((a, l) => a + l.costo, 0));
  }
  return { valores, etiquetas };
}

export function agrupar(llamadas: Llamada[], clave: (l: Llamada) => string) {
  const m = new Map<string, { nombre: string; costo: number; minutos: number; llamadas: number }>();
  for (const l of llamadas) {
    const k = clave(l) || 'Sin dato';
    const g = m.get(k) ?? { nombre: k, costo: 0, minutos: 0, llamadas: 0 };
    g.costo += l.costo;
    g.minutos += l.duracion / 60;
    g.llamadas += 1;
    m.set(k, g);
  }
  return [...m.values()].sort((a, b) => b.costo - a.costo || b.llamadas - a.llamadas);
}

export function totalesTarificador(llamadas: Llamada[]) {
  const sal = salientesConCosto(llamadas);
  const costo = sal.reduce((a, l) => a + l.costo, 0);
  const minutos = sal.reduce((a, l) => a + l.duracion, 0) / 60;
  return {
    costo,
    minutos: Math.round(minutos),
    costoMinuto: minutos ? costo / minutos : 0,
    llamadas: llamadas.filter((l) => l.direccion !== 'INTERNA').length,
    contestadas: llamadas.filter(esContestada).length,
  };
}

/* ---------------- supervisor: equipo hoy ---------------- */

export function equipo(llamadas: Llamada[]) {
  const m = new Map<string, Llamada[]>();
  for (const l of llamadas) {
    if (!l.agente) continue;
    m.set(l.agente, [...(m.get(l.agente) ?? []), l]);
  }
  return [...m.entries()]
    .map(([agente, ls]) => ({ agente, ...kpis(ls), ultima: Math.max(...ls.map(ts)) }))
    .sort((a, b) => b.total - a.total);
}

/* ---------------- tareas ---------------- */

export function seguimientosDeHoy(tareas: Tarea[], { miembroId, ahora = new Date() }: { miembroId?: string; ahora?: Date } = {}) {
  const fin = new Date(ahora);
  fin.setHours(23, 59, 59, 999);
  return tareas
    .filter((t) => t.estado !== 'DONE' && (!miembroId || !t.asignadoId || t.asignadoId === miembroId))
    .filter((t) => !t.vence || new Date(t.vence) <= fin)
    .sort((a, b) => (a.vence ? new Date(a.vence).getTime() : Infinity) - (b.vence ? new Date(b.vence).getTime() : Infinity));
}

/* ---------------- Hoy por periodos ---------------- */

// En vivo = hoy (se refresca solo); 7 d / 30 d / 12 m = ventanas que terminan ahora.
export type PeriodoHoy = 'vivo' | 7 | 30 | 365;

export type VentanaHoy = { desde: number; hasta: number; antes: [number, number] | null };

const inicioDia = (t: number) => {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

// Ventana del periodo y la del periodo anterior para comparar (en vivo: ayer hasta esta misma hora).
// 12 meses no trae periodo anterior: haria falta otro anio de llamadas.
export function ventanaHoy(p: PeriodoHoy, ahora = Date.now()): VentanaHoy {
  const hoy0 = inicioDia(ahora);
  if (p === 'vivo') {
    const ayer = new Date(hoy0);
    ayer.setDate(ayer.getDate() - 1);
    return { desde: hoy0, hasta: ahora, antes: [ayer.getTime(), ahora - DIA] };
  }
  if (p === 365) {
    const d = new Date(hoy0);
    return { desde: new Date(d.getFullYear(), d.getMonth() - 11, 1).getTime(), hasta: ahora, antes: null };
  }
  const desde = new Date(hoy0);
  desde.setDate(desde.getDate() - (p - 1));
  const antesDesde = new Date(desde);
  antesDesde.setDate(antesDesde.getDate() - p);
  return { desde: desde.getTime(), hasta: ahora, antes: [antesDesde.getTime(), desde.getTime() - 1] };
}

export const enVentana = (llamadas: Llamada[], desde: number, hasta: number) =>
  llamadas.filter((l) => {
    const t = ts(l);
    return t >= desde && t <= hasta;
  });

export type SerieHoy = { etiquetas: string[]; largos: string[]; total: number[]; contestadas: number[] };

// Barras del periodo: en vivo una por hora de hoy (7 a 19 h, o mas si hubo llamadas antes o despues),
// 7 y 30 dias una por dia, 12 meses una por mes.
export function serieHoy(llamadas: Llamada[], p: PeriodoHoy, ahora = Date.now()): SerieHoy {
  const v = ventanaHoy(p, ahora);
  const dentro = enVentana(llamadas, v.desde, v.hasta).filter((l) => l.direccion !== 'INTERNA');
  const cubos: { etq: string; largo: string; clave: string }[] = [];
  let claveDe: (t: number) => string;
  if (p === 'vivo') {
    const horas = dentro.map((l) => new Date(ts(l)).getHours());
    const desde = Math.min(7, ...horas), hasta = Math.max(19, ...horas);
    for (let h = desde; h <= hasta; h++) cubos.push({ etq: String(h), largo: `de ${h}:00 a ${h}:59`, clave: String(h) });
    claveDe = (t) => String(new Date(t).getHours());
  } else if (p === 365) {
    const d0 = new Date(v.desde);
    for (let i = 0; i < 12; i++) {
      const m = new Date(d0.getFullYear(), d0.getMonth() + i, 1);
      cubos.push({ etq: m.toLocaleDateString('es-CO', { month: 'short' }).replace('.', ''), largo: m.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' }), clave: `${m.getFullYear()}-${m.getMonth()}` });
    }
    claveDe = (t) => {
      const x = new Date(t);
      return `${x.getFullYear()}-${x.getMonth()}`;
    };
  } else {
    for (let i = 0; i < p; i++) {
      const d = new Date(v.desde);
      d.setDate(d.getDate() + i);
      cubos.push({
        etq: p === 7 ? d.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric' }).replace('.', '').replace(',', '') : String(d.getDate()),
        largo: d.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' }),
        clave: d.toDateString(),
      });
    }
    claveDe = (t) => new Date(t).toDateString();
  }
  const pos = new Map(cubos.map((c, i) => [c.clave, i]));
  const total = cubos.map(() => 0), contestadas = cubos.map(() => 0);
  for (const l of dentro) {
    const i = pos.get(claveDe(ts(l)));
    if (i == null) continue;
    total[i]++;
    if (esContestada(l)) contestadas[i]++;
  }
  return { etiquetas: cubos.map((c) => c.etq), largos: cubos.map((c) => c.largo), total, contestadas };
}

// Tareas pendientes que no estan vencidas (vencen hoy o despues, o no tienen fecha).
export function tareasAlDia(tareas: Tarea[], { miembroId, ahora = new Date() }: { miembroId?: string; ahora?: Date } = {}) {
  const hoy0 = inicioDia(ahora.getTime());
  const mias = tareas.filter((t) => t.estado !== 'DONE' && (!miembroId || t.asignadoId === miembroId));
  const alDia = mias.filter((t) => !t.vence || new Date(t.vence).getTime() >= hoy0).length;
  return { total: mias.length, alDia, pct: mias.length ? (100 * alDia) / mias.length : 100 };
}
